import { ThemeResponseDto } from '../dto/response/theme-response.dto';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import themeService from '../services/theme.service';
import { ThemeSaveRequestDto } from '../dto/request/theme-save-request.dto';
import { ThemeUpdateRequestDto } from '../dto/request/theme-update-request.dto';
import { AxiosError } from 'axios';
import { showError, showSuccess } from '../../../../utils/Notifications';
import { LocalesLanguagesConstants } from '../../../../locales/locales';

const initialState = {
  status: 'idle',
  themes: [] as ThemeResponseDto[] | null,
  theme: {} as ThemeResponseDto | null,
  error: null as string | null,
};

export const fetchThemes = createAsyncThunk('themes/fetchThemes', async () => {
  return await themeService.fetchThemes();
});

export const fetchThemeById = createAsyncThunk(
  'themes/fetchThemeById',
  async (params: { id: string; language: LocalesLanguagesConstants }) => {
    return await themeService.fetchThemeById(params.id, params.language);
  }
);

export const saveTheme = createAsyncThunk(
  'themes/saveTheme',
  async (params: {
    saveRequest: ThemeSaveRequestDto;
    language: LocalesLanguagesConstants;
  }) => {
    return await themeService.saveTheme(params.saveRequest, params.language);
  }
);

export const updateTheme = createAsyncThunk(
  'themes/updateTheme',
  async (params: {
    updateRequest: ThemeUpdateRequestDto;
    language: LocalesLanguagesConstants;
  }) => {
    return await themeService.updateTheme(
      params.updateRequest,
      params.language
    );
  }
);

export const deleteTheme = createAsyncThunk(
  'themes/deleteTheme',
  async (existingId: string, { rejectWithValue, dispatch }) => {
    try {
      await themeService.deleteTheme(existingId);
      dispatch(fetchThemes());
    } catch (e) {
      return rejectWithValue((e as AxiosError).response?.data ?? 'Failed');
    }
  }
);

const themeSlice = createSlice({
  name: 'themeSlice',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchThemes.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchThemes.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.themes = action.payload;
      })
      .addCase(fetchThemes.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error?.message ?? null;
      })
      .addCase(fetchThemeById.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchThemeById.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.theme = action.payload;
      })
      .addCase(fetchThemeById.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error?.message ?? null;
      })
      .addCase(saveTheme.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(saveTheme.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.themes?.push(action.payload);
        showSuccess('Theme added successfully.');
      })
      .addCase(saveTheme.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error?.message ?? null;
      })
      .addCase(updateTheme.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(updateTheme.fulfilled, (state, action) => {
        state.status = 'succeeded';
        if (state.themes) {
          const themeIndex = state.themes.findIndex(
            (theme) => theme.id === action.payload.id
          );
          if (themeIndex !== -1) {
            state.themes[themeIndex] = action.payload;
          }
        }
      })
      .addCase(updateTheme.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error?.message ?? null;
      })
      .addCase(deleteTheme.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(deleteTheme.fulfilled, (state) => {
        state.status = 'succeeded';
        showSuccess('Theme deleted successfully.');
      })
      .addCase(deleteTheme.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error?.message ?? null;
        showError(`Delete theme failed. ${action.payload as string}`);
      });
  },
});

export default themeSlice.reducer;
